<template>
  <div class="flex flex-col h-full">
    <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
      <template slot="title">{{ item.name }}<small class="text-xs ml-2">Application Form</small></template>
    </page-header>
    <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
    <div class="flex xl:flex-row lg:flex-col md:flex-col sm:flex-col flex-grow">
      <div class="xl:w-3/4 lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6 border-r border-gray-400 bg-gray-200">
        <div v-if="!loading" v-show="activeTab === 'details'" class="mt-2 grid grid-cols-12 row-gap-5 col-gap-4 sm:grid-cols-12">
          <div class="sm:col-span-12">
            <table class="w-full mb-4 internal-table">
              <thead>
                <tr>
                  <th colspan="2">Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="w-1/4">Name</td>
                  <td>{{ item.name }}</td>
                </tr>
                <tr>
                  <td>Limit</td>
                  <td>{{ item.limit }}</td>
                </tr>
                <tr>
                  <td>Allocated Amount</td>
                  <td>{{ item.budget }}</td>
                </tr>
                <tr>
                  <td>Application Status</td>
                  <td v-if="item.active"><span class="text-green-500">Active</span></td>
                  <td v-if="!item.active"><span class="text-red-500">Inactive</span></td>
                </tr>
                <tr>
                  <td>QR Code</td>
                  <td v-if="item.qr_code"><span class="text-green-500">On</span></td>
                  <td v-else><span class="text-red-500">Off</span></td>
                </tr>
                <tr>
                  <td>Hide Form Responses</td>
                  <td v-if="canEditForm">
                    <toggle-button
                      :value="!!item.hide_form_responses"
                      :sync="true"
                      :labels="{checked: 'On', unchecked: 'Off'}"
                      :color="{checked: '#38a169', unchecked: '#CCCCCC'}"
                      :width="70"
                      :height="26"
                      @change="updateHideFormResponses"
                    />
                  </td>
                  <td v-else-if="item.hide_form_responses"><span class="text-green-500">On</span></td>
                  <td v-else><span class="text-red-500">Off</span></td>
                </tr>
                <tr>
                  <td>Account Code</td>
                  <td>{{ item.account_code }}</td>
                </tr>
                <tr>
                  <td>Funding Account BSB</td>
                  <td>{{ item.funding_account_bsb }}</td>
                </tr>
                <tr>
                  <td>Funding Account Number</td>
                  <td>{{ item.funding_account_number }}</td>
                </tr>
                <tr>
                  <td>Default Account BSB</td>
                  <td>{{ item.default_account_bsb }}</td>
                </tr>
                <tr>
                  <td>Default Account Number</td>
                  <td>{{ item.default_account_number }}</td>
                </tr>
                <tr>
                  <td>Xero Account</td>
                  <td v-if="tenantName">{{ tenantName }}</td>
                  <td v-else>N/A</td>
                </tr>
                <tr>
                  <td>Documents</td>
                  <td v-if="item.documents && item.documents.length">
                    <a v-for="(file, index) in item.documents" :key="file.path || index" :href="S3URL + '/' + file.path" target="_blank" class="block text-gray-800 hover:text-default-500">{{ file.filename }}</a>
                  </td>
                  <td v-else>None</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="!loading" v-show="activeTab === 'members'" class="mt-2 grid grid-cols-12 row-gap-5 col-gap-4 sm:grid-cols-12">
          <div class="sm:col-span-12">
            <MemberList :application-form-id="id" :qr-code="item.qr_code" @members="setSelectedMembers"/>
          </div>
        </div>
        <div v-if="!loading" v-show="activeTab === 'logs'" class="mt-1 grid grid-cols-1 row-gap-2 col-gap-4 sm:grid-cols-12">

        </div>
        <notes v-if="!loading"
          v-show="activeTab === 'notes'"
          :id="item.id"
          :notes="item.notes"
          model="ApplicationForm"
        />
      </div>
      <div class="xl:w-1/4 lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6">
        <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12" v-if="!loading">
          <div class="sm:col-span-12">
            <table class="w-full mb-6 internal-table">
              <thead>
              <tr>
                <th colspan="2">Meta</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Last Updated</td>
                <td>{{ new Date(item.meta.updated_at) | moment('DD/MM/YYYY h:mm a') }}</td>
              </tr>
              <tr>
                <td>Created At</td>
                <td>{{ new Date(item.meta.created_at) | moment('DD/MM/YYYY h:mm a') }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <SendPaymentModal v-if="showBulkPaymentModal" @close="showBulkPaymentModal = false" heading="Send Bulk Payment">
      <template #default>
        <div v-if="!hasFromAccountDetails">
          <p>
            Funding Bank Account Details are missing you must add them before you can send a payment.
          </p>
        </div>
        <table v-else class="text-sm w-full">
          <tr>
            <td class="font-bold pr-4 pb-1">Amount Per Member:</td>
            <td class="py-2">
              <input type="number" v-model="bulkAmount" class="border border-gray-500 px-1 py-1 w-full focus:outline-none" />
            </td>
          </tr>
          <tr>
            <td class="font-bold pr-4 pb-2 whitespace-no-wrap">From Account:</td>
            <td class="py-2 pl-1 align-middle">
              <strong>BSB:</strong> {{ item.funding_account_bsb }} <strong>Account:</strong> {{ item.funding_account_number }}
            </td>
          </tr>
          <tr v-if="item.qr_code">
            <td class="font-bold pr-4 pb-1">Only Members with Scanned QR codes:</td>
            <td class="py-2 flex items-center justify-start">
              <span>
                  <input type="checkbox" v-model="onlyScannedQr" />
              </span>
            </td>
          </tr>
          <tr>
            <td class="font-bold pr-4 pb-1">Send to Xero:</td>
            <td class="py-2 flex items-center justify-start">
              <span>
                  <input type="checkbox" v-model="postPaymentToXero" />
              </span>
            </td>
          </tr>
        </table>
      </template>
      <template #actions>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto" v-if="bulkAmount > 0 && item.funding_account_bsb && item.funding_account_number">
          <button type="button"
            class="inline-flex justify-center w-full border border-transparent px-4 py-2 bg-default-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-default-700 focus:outline-none focus:border-default-700 focus:shadow-outline-default transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            @click="sendBulkPayment"
          >
              Submit
           </button>
         </span>
        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <button type="button"
            class="inline-flex justify-center w-full border border-gray-500 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-900 shadow-sm hover:bg-gray-300 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            @click="showBulkPaymentModal = false"
          >
          Cancel
          </button>
        </span>
      </template>
    </SendPaymentModal>
  </div>
</template>

<script>
import Tabs from '../../mixins/Tabs'
import axios from 'axios'
import Notes from '@/components/Notes'
import MemberList from './application-form-components/MemberList'
import SendPaymentModal from '@/views/applicationForms/modals/SendPaymentModal.vue'

export default {
  name: 'ApplicationView',
  components: {
    SendPaymentModal,
    MemberList,
    Notes
  },
  props: {
    id: { required: true },
    return: { required: false, default: '/application-forms' },
    page: { required: false, default: 'application-forms' }
  },
  mixins: [
    Tabs
  ],
  data: function () {
    return {
      startTab: null,
      loading: true,
      item: {},
      pageActions: {},
      selectedMembers: 0,
      showBulkPaymentModal: false,
      bulkAmount: null,
      postPaymentToXero: true,
      onlyScannedQr: false,
      tenantName: '',
      S3URL: process.env.VUE_APP_S3_URL
    }
  },
  methods: {
    setSelectedMembers: function (payload) {
      this.selectedMembers = payload
    },
    getData: function () {
      this.$Progress.start()

      axios.get(process.env.VUE_APP_API_URL + '/application-forms/' + this.id, {
        params: {
          include: 'xero-tenants'
        }
      }).then(response => {
        this.item = response.data

        this.pageActions = {
          buttons: [
            {
              label: 'Bulk Payment',
              icon: 'coins',
              type: 'method',
              method: 'openBulkPaymentModal',
              iconClass: 'text-gray-700',
              display: !!(this.$store.getters.permissions.indexOf('application-forms.bulk-payment') !== -1 || this.$store.getters.nimdarepus)
            },
            {
              label: 'Edit',
              icon: 'edit',
              type: 'router-link',
              link: '/application-forms/' + this.id + '/edit',
              iconClass: 'text-gray-700',
              display: !!(this.$store.getters.permissions.indexOf('application-forms.edit') !== -1 || this.$store.getters.nimdarepus)
            },
            {
              label: 'Close',
              icon: 'times',
              type: 'router-link',
              iconClass: 'text-red-500',
              link: this.return ? this.return : '/application-forms'
            }
          ],
          additional: [
            {
              label: 'Delete',
              icon: 'trash-alt',
              type: 'method',
              method: 'deleteModal',
              iconClass: 'text-red-500',
              display: !!(this.$store.getters.permissions.indexOf('application-forms.destroy') !== -1 || this.$store.getters.nimdarepus)
            }
          ]
        }

        this.tenantName = this.item.tenants.find((tenant) => tenant.id === this.item.xero_tenant).tenantName

        this.$Progress.finish()
        this.loading = false
      }).catch(() => {
        this.$Progress.finish()
        this.loading = false
      })
    },
    methodHandler: function (method) {
      this[method]()
    },
    actionHandler: function (data) {
      this[data.method](data)
    },
    openBulkPaymentModal: function () {
      this.onlyScannedQr = false
      this.showBulkPaymentModal = true
    },
    updateHideFormResponses: function ({ value }) {
      const previous = !!this.item.hide_form_responses
      // Update immediately so the toggle matches the click, then revert if the save fails.
      this.item.hide_form_responses = value

      axios.put(process.env.VUE_APP_API_URL + '/application-forms/' + this.id, {
        hide_form_responses: value
      }).then(response => {
        this.$notify({
          group: 'main',
          position: 'bottom right',
          type: 'success',
          title: 'Success',
          text: response.data.message,
          duration: 5000
        })
      }).catch(error => {
        this.item.hide_form_responses = previous

        this.$notify({
          group: 'main',
          position: 'bottom right',
          type: 'error',
          title: 'Error',
          text: error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Unable to update form',
          duration: 5000
        })
      })
    },
    sendBulkPayment: function () {
      this.$store.commit('TOGGLE_BACKDROP', { open: true })

      axios.post(process.env.VUE_APP_API_URL + '/application-forms/' + this.id + '/bulk-payment', {
        amount: this.bulkAmount,
        xero: this.postPaymentToXero,
        only_scanned_qr: this.onlyScannedQr
      }).then(response => {
          this.$Progress.finish()
          this.showBulkPaymentModal = false
          this.getData()

          this.$notify({
            group: 'main',
            position: 'bottom right',
            type: 'success',
            title: 'Success',
            text: response.data.message,
            duration: 5000
          })
        }).catch((error) => {
          this.$Progress.finish()
          this.showBulkPaymentModal = false

          this.$notify({
            group: 'main',
            position: 'bottom right',
            type: 'error',
            title: 'Error',
            text: error.response.data.message,
            duration: 5000
        })
      }).finally(() => {
        this.$store.commit('TOGGLE_BACKDROP', { open: false })
      })
    }
  },
  mounted() {
    this.getData()
  },
  computed: {
    tabs() {
      return [
        {
          name: 'Details',
          slug: 'details'
        },
        {
          name: 'Members' + ' (' + (this.selectedMembers) + ')',
          slug: 'members'
        },
        {
          name: 'Notes',
          slug: 'notes'
        }
      ]
    },
    hasFromAccountDetails(){
      return this.item.funding_account_bsb && this.item.funding_account_number;
    },
    canEditForm() {
      return !!(this.$store.getters.permissions.indexOf('application-forms.edit') !== -1 || this.$store.getters.nimdarepus)
    }
  }
}
</script>
